import type { CurrencyBalanceDto } from '~/types/api/wallet'
import { clearFundPwdSession } from '~/utils/fundPasswordSession'
import {
  currencyCodeLabel,
  currencyIconPath,
  formatBaseAmt,
  isBaseCurrency,
  parseMoney,
  type BaseCurrency,
} from '~/utils/transfer/money'

export type { BaseCurrency }

type ClearSessionReason = 'manual' | 'expired'

/** 401 并发静默窗口（ms） */
const UNAUTHORIZED_TOAST_SUPPRESS_MS = 800
let suppressUnauthorizedToastUntil = 0

export interface UserProfile {
  uid: string
  nickname: string
  /** 绑定手机；空串表示未绑定（→ 用户信息接口） */
  phone: string
  /** 绑定邮箱；空串表示未绑定（→ 用户信息接口） */
  email: string
  /** 是否已设置资金密码（不存明文；→ 用户信息接口） */
  hasFundPassword: boolean
  /** 是否账号密码登录 */
  isPassword: boolean
}

/** 钱包余额态（对应 GET /wallet/balances → CurrencyBalance） */
export interface WalletState {
  /**
   * 账本本位币（仅由 /wallet/balances.base_currency 回填，即租户本位币）；
   * 余额数值始终以它记账
   */
  ledgerCurrency: BaseCurrency
  /** 主展示余额（可用额 available_display，账本币值） */
  balance: string
  /** 可用余额展示字符串（账本币值） */
  available: string
}

export interface RewardBadges {
  salary: number
  task: number
  rebate: number
  total: number
}

export interface VipState {
  level: number
  /** 当前等级展示名（如 VIP 3） */
  name: string
  nextLevel: number
  /** 进度 0–100 */
  progressPct: number
  /** 距下一级缺口金额（接口原串） */
  gapAmount: string
  /** 缺口/成长金额所用本位币 */
  currency: string
  /** 已达最高等级（progress_pct ≥ 100） */
  isMax: boolean
  /** 明文徽章地址（加密图标不落库） */
  iconUrl: string
}

export type KycStatus = 'none' | 'pending' | 'approved' | 'rejected'

/** KYC 已认证资料（来自 /kyc/status） */
export interface KycProfile {
  doc_type?: 'id_card' | 'passport'
  real_name?: string
  id_number?: string
  /** 证件到期日 YYYY-MM-DD（纯日期，原样显示；接口字段 valid_until） */
  expiry_date?: string
  long_term?: boolean
  /** 审核被驳回时的原因 */
  reject_reason?: string
  /** 最近一次提交时刻（时刻串 2026-09-28T02:30:00.000Z） */
  submitted_at?: string
  /** 最近一次审核时刻（时刻串） */
  reviewed_at?: string
}

const EMPTY_PROFILE: UserProfile = {
  uid: '',
  nickname: '',
  phone: '',
  email: '',
  hasFundPassword: false,
  isPassword: false,
}

const EMPTY_WALLET: WalletState = {
  ledgerCurrency: 'USDT',
  balance: '0.00',
  available: '0.00',
}

const EMPTY_VIP: VipState = {
  level: 0,
  name: 'VIP 0',
  nextLevel: 1,
  progressPct: 0,
  gapAmount: '0',
  currency: '',
  isMax: false,
  iconUrl: '',
}

const EMPTY_BADGES: RewardBadges = {
  salary: 0,
  task: 0,
  rebate: 0,
  total: 0,
}

export const useUserStore = defineStore(
  'user',
  () => {
    const isLoggedIn = ref(false)
    const token = ref('')
    const user = reactive<UserProfile>({ ...EMPTY_PROFILE })
    const wallet = reactive<WalletState>({ ...EMPTY_WALLET })
    const vip = reactive<VipState>({ ...EMPTY_VIP })
    /** 奖励入口红点（/me.badges 预填） */
    const rewardBadges = reactive<RewardBadges>({ ...EMPTY_BADGES })

    /** 未读站内信数（/me.unread.notice 预填；列表/已读后由 useNotices 回写） */
    const noticeUnread = ref(0)
    /** 安全中心是否已开启 */
    const securityOn = ref(true)
    /**
     * 后端下发：是否开启 KYC 认证功能（true=提现显示认证卡片，false=全部隐藏）
     * 来源：GET /me.kyc_required
     */
    const kycRequired = ref<boolean>(false)
    const kycStatus = ref<KycStatus>('none')
    /** 已认证资料（后端 /kyc/status 回填） */
    const kycProfile = ref<KycProfile>({})

    /** 展示币种，与账本币相同，来自 /wallet/balances.base_currency */
    const baseCurrency = computed<BaseCurrency>(() =>
      isBaseCurrency(wallet.ledgerCurrency) ? wallet.ledgerCurrency : EMPTY_WALLET.ledgerCurrency,
    )
    /** 币种图标，跟账本币 /wallet/balances.base_currency */
    const currencyIcon = computed(() => currencyIconPath(baseCurrency.value))
    /** 余额旁币种图标：只跟 /wallet/balances.base_currency（账本币） */
    const ledgerCurrencyIcon = computed(() => currencyIcon.value)
    /** 余额旁短标签：¥ / USDT */
    const currencySym = computed(() => currencyCodeLabel(baseCurrency.value))
    const vipGapLabel = computed(() => {
      const cur = isBaseCurrency(vip.currency) ? vip.currency : baseCurrency.value
      return formatBaseAmt(parseMoney(vip.gapAmount), cur)
    })
    /** KYC 功能是否开启（后端 kyc_required=true 时显示认证卡片/菜单行） */
    const showKycRow = computed(() => kycRequired.value)

    const setProfile = (patch: Partial<UserProfile>) => {
      ;(Object.keys(patch) as (keyof UserProfile)[]).forEach(key => {
        const value = patch[key]
        if (value !== undefined) (user[key] as typeof value) = value
      })
    }

    const setVip = (patch: Partial<VipState>) => {
      Object.assign(vip, patch)
      if (patch.nextLevel == null) vip.nextLevel = vip.isMax ? vip.level : vip.level + 1
    }

    const setRewardBadges = (patch: Partial<RewardBadges>) => {
      Object.assign(rewardBadges, patch)
      if (patch.total == null) {
        rewardBadges.total = rewardBadges.salary + rewardBadges.task + rewardBadges.rebate
      }
    }

    const setLoggedIn = (value: boolean) => {
      isLoggedIn.value = value
    }

    const setToken = (value: string) => {
      token.value = value
      if (value) suppressUnauthorizedToastUntil = 0
    }

    /** 回填 /wallet/balances 返回的余额与本位币 */
    const setWallet = (dto: CurrencyBalanceDto) => {
      const code = dto.base_currency?.toUpperCase()
      if (isBaseCurrency(code)) wallet.ledgerCurrency = code
      wallet.balance = dto.available_display
      wallet.available = dto.available_display
    }

    /**
     * 退出登录：清除鉴权与登录后拉取的会话数据。
     * 保留租户级配置（kycRequired）与安全开关偏好（securityOn）。
     */
    const clearSession = (reason: ClearSessionReason = 'manual') => {
      console.warn('[auth] clearSession:', reason)
      isLoggedIn.value = false
      token.value = ''
      Object.assign(user, EMPTY_PROFILE)
      Object.assign(wallet, EMPTY_WALLET)
      Object.assign(vip, EMPTY_VIP)
      Object.assign(rewardBadges, EMPTY_BADGES)
      noticeUnread.value = 0
      kycRequired.value = false
      kycStatus.value = 'none'
      kycProfile.value = {}
      // 资金密码会话缓存（useSecurity）；401 / 退出时一并清掉
      clearFundPwdSession()
      // VIPPAY 最近使用账号（localStorage）；退出登录或 token 失效时清掉
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('vippay-recent-accounts')
      }
      if (reason === 'manual') suppressUnauthorizedToastUntil = 0
    }

    /** 401 且仍有会话：清登录态；返回是否应静默（不 toast） */
    const expireOnUnauthorized = (): boolean => {
      const now = Date.now()
      const hadSession = Boolean(token.value || isLoggedIn.value)

      if (hadSession) {
        suppressUnauthorizedToastUntil = now + UNAUTHORIZED_TOAST_SUPPRESS_MS
        clearSession('expired')
        return true
      }

      return now < suppressUnauthorizedToastUntil
    }

    /** 额度转换成功后回填中心钱包展示额（与 /wallet/balances.available_display 同形） */
    const setAvailableDisplay = (display: string) => {
      if (!display) return
      wallet.balance = display
      wallet.available = display
    }

    return {
      isLoggedIn,
      token,
      user,
      wallet,
      vip,
      rewardBadges,
      noticeUnread,
      securityOn,
      kycRequired,
      kycStatus,
      kycProfile,
      currencyIcon,
      ledgerCurrencyIcon,
      currencySym,
      baseCurrency,
      vipGapLabel,
      showKycRow,
      setProfile,
      setVip,
      setRewardBadges,
      setLoggedIn,
      setToken,
      setWallet,
      clearSession,
      expireOnUnauthorized,
      setAvailableDisplay,
    }
  },
  {
    persist: {
      // cookies：SSR 可读，避免 localStorage 导致登录态水合不一致
      storage: piniaPluginPersistedstate.cookies({
        sameSite: 'lax',
        maxAge: 60 * 60 * 24 * 365,
      }),
      pick: [
        'isLoggedIn',
        'token',
        'user',
        // 余额为服务端态，不持久化（避免 cookie 脏余额）；本位币由接口回填
        'vip',
        'securityOn',
        'kycRequired',
        'kycStatus',
        'kycProfile',
      ],
      afterHydrate: ({ store }) => {
        // 兼容旧 cookie：补齐 /me 新增的 vip 字段
        Object.assign(store.vip, { ...EMPTY_VIP, ...store.vip })
        // 兼容旧 cookie：kycRequired 曾为 'never'|'onWithdraw'|'always' 字符串枚举，
        // 现改为 boolean；脏值一律回落 false，随后由 GET /kyc/status 重新回填
        const legacyKycRequired = (store as unknown as { kycRequired: unknown }).kycRequired
        if (typeof legacyKycRequired !== 'boolean') store.kycRequired = false
      },
    },
  },
)
